import { Flex, Box, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { DateRangePicker, Icons, Typography, useFlowBuilderSession } from "@reconvert/react-ui-component";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { handleDateRangePicker, selectAppState, setActivePage } from "../../app/slice/appSlice";
import { goBack, navigateTo, resetNavigation } from "../../app/slice/mobileSlice";
import { useTranslation } from "react-i18next";
import { PageNames, LinkItem } from "../../app/slice/appSlice.model";
import { MobileScreen } from "../../app/slice/mobileSlice";
import { useMemo, useState } from "react";
import { selectCondition, setSelectedSlot, setSelectedVariantId } from "../../app/slice/conditionSlice";
import useUnsavedBar from "../../hooks/useUnsavedBar";

interface MobileHeaderProps {
  screen: MobileScreen;
}

const MobileHeader = ({ screen }: MobileHeaderProps) => {
  const dispatch = useAppDispatch();
  const { preventAction } = useUnsavedBar();
  const { t } = useTranslation();
  const { setSelectedConditionInSession, setPageInSession, setSelectedVariantInSession } = useFlowBuilderSession();
  const { activePage, sideBarSections, dateRange } = useAppSelector(selectAppState);
  const [shortcut, setShortCut] = useState<string | null>(t("Last 30 days"));

  const allLocations = useMemo(() => {
    return sideBarSections
      .filter(s => s.enabled && s.links.length > 0)
      .flatMap(s => s.links)
      .filter(link => !link.disabled);
  }, [sideBarSections]);

  const handleBack = () => {
    if (screen === "conditions") {
      dispatch(setActivePage(undefined));
      setPageInSession(undefined);
      setSelectedConditionInSession(undefined);
      setSelectedVariantInSession(undefined);
      dispatch(resetNavigation());
    } else if (screen === "slots") {
      dispatch(selectCondition(null));
      dispatch(setSelectedSlot(null));
      dispatch(setSelectedVariantId(null));
      setSelectedConditionInSession(undefined);
      setSelectedVariantInSession(undefined);
      dispatch(navigateTo("conditions"));
    } else {
      dispatch(goBack());
    }
  };

  const handleLocationChange = (link: LinkItem) => {
    if (link.type === activePage) return;

    setPageInSession(link.type);
    dispatch(setActivePage(link.type));
    dispatch(selectCondition(null));
    dispatch(setSelectedVariantId(null));
    dispatch(setSelectedSlot(null));
    dispatch(navigateTo("conditions"));
  };

  const pageTitle = activePage ? t(PageNames[activePage] || "") : "";
  const showFlowSelector = (screen === "conditions" || screen === "slots") && allLocations.length > 1;
  const showDateRange = screen === "slots";

  return (
    <Flex
      alignItems="center"
      px="12px"
      py="10px"
      gap="10px"
      borderBottom="1px solid"
      borderColor="neutrals.300"
      bg="white"
      position="sticky"
      top={0}
      zIndex={10}
    >
      <Box
        as="button"
        onClick={() => preventAction(() => handleBack())}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        w="36px"
        h="36px"
        borderRadius="6px"
        _hover={{ bg: "neutrals.100" }}
      >
        <Icons.CaretLeft color="neutrals.700" />
      </Box>

      {showFlowSelector ? (
        <Menu>
          <MenuButton
            as={Box}
            cursor="pointer"
            flex={1}
            minWidth={0}
            border="1px solid"
            borderColor="neutrals.400"
            borderRadius="6px"
            px="12px"
            py="8px"
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Typography variant="body" fontWeight={500} isTruncated>
                {pageTitle}
              </Typography>
              <ChevronDownIcon color="neutrals.600" boxSize="18px" flexShrink={0} ml="8px" />
            </Flex>
          </MenuButton>
          <MenuList zIndex={20} maxH="60vh" overflowY="auto">
            {allLocations.map(link => {
              const isActive = link.type === activePage;

              return (
                <MenuItem
                  key={link.type}
                  onClick={() => preventAction(() => handleLocationChange(link))}
                  bg={isActive ? "primary.50" : undefined}
                  _hover={{ bg: isActive ? "primary.100" : "neutrals.100" }}
                  _focus={{ bg: isActive ? "primary.50" : undefined }}
                >
                  <Flex alignItems="center" gap="10px">
                    <Box flexShrink={0}>{link.icon}</Box>
                    <Typography variant="body" fontWeight={isActive ? 600 : 400}>
                      {t(link.name)}
                    </Typography>
                  </Flex>
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      ) : (
        <Typography variant="h2-r-medium" flex={1} isTruncated>
          {pageTitle}
        </Typography>
      )}

      {showDateRange && (
        <DateRangePicker
          id="date"
          name="MM / DD"
          defaultFromDate={new Date(dateRange.startDate)}
          defaultToDate={new Date(dateRange?.endDate)}
          onDateValueChange={(startDate: Date | undefined, endDate: Date | undefined) => {
            if (startDate && endDate) {
              dispatch(handleDateRangePicker({ startDate: startDate, endDate: endDate }));
            }
          }}
          size="sm"
          showPredefinedRanges={false}
          isMobile={true}
          shortcut={shortcut}
          setShortCut={setShortCut}
        />
      )}
    </Flex>
  );
};

export default MobileHeader;
